import {
	ActionType,
	AdministrativeAuthorizationType,
	ClientType,
	ConstructionSiteDocumentType,
	SubmissionWithOperationDetailsPayload,
	WarrantyType,
} from "@dune/types";
import {
	Button,
	H3,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { useFormContext } from "react-hook-form";
import {
	useLinkFileToConstructionSiteSubmission,
	useUnlinkFileFromConstructionSiteSubmission,
	useDeleteFileFromConstructionSiteReception,
} from "../../api/mutations/constructionSiteSubmission.mutations";
import { useBinderWarrantiesTypesSelected } from "../../api/queries/constructionSiteSubmission.queries";
import { type ReceptionOperationFile } from "../../api/queries/constructionSiteReception.queries";
import { FileStatusChip } from "../common/files/FileStatusChip";
import { PreviewFileLink } from "../common/files/PreviewFileLink";
import { AddDocumentButton } from "../constructionSites/submission/AddDocumentButton";
import { TableCellWithTooltip } from "../shared/TableCellWithTooltip";
import { AdministrativeAuthorizationFormData } from "./administrativeAuthorizationForm/useAdministrativeAuthorizationForm";
import { LinkBroken02 } from "@untitled-ui/icons-react";
import { useLinkFileToConstructionSiteReception } from "../../api/mutations/constructionSiteReception.mutations";

const submissionOperationSections = [
	"MOA",
	"OPERATION_INFORMATION",
	"ADMINISTRATIVE_AUTHORIZATION",
	"OPERATION_DATES",
] as const;
export type SubmissionOperationSection =
	(typeof submissionOperationSections)[number];

interface OperationDocumentsProps {
	constructionSiteId: number;
	section: SubmissionOperationSection;
	submissionOperationDetails: SubmissionWithOperationDetailsPayload;
	receptionOperationFiles?: ReceptionOperationFile[];
	showHeader?: boolean;
	disabled?: boolean;
	isReception?: boolean;
}

export const OperationDocuments = ({
	constructionSiteId,
	section,
	submissionOperationDetails,
	receptionOperationFiles,
	showHeader = true,
	disabled = false,
	isReception = false,
}: OperationDocumentsProps) => {
	const { data: binderWarrantiesTypesSelected } =
		useBinderWarrantiesTypesSelected(constructionSiteId);

	const form = useFormContext<AdministrativeAuthorizationFormData>();
	const { watch } = form;

	const administrativeAuthorizationType = watch(
		"administrativeAuthorizationType",
	);

	const clientDetails =
		submissionOperationDetails.constructionSite.constructionSiteClientDetails;
	const moaClientType = clientDetails?.clientIsMOA
		? clientDetails.clientType
		: clientDetails?.moaClientType;
	const documentTypes = getDocumentTypesForSection(
		section,
		{
			moaClientType: moaClientType || undefined,
			administrativeAuthorizationType,
			operationTypes:
				submissionOperationDetails.constructionSite.constructionSiteDetails
					?.constructionSiteAction?.types,
			socialLandlordRental:
				submissionOperationDetails.constructionSite.constructionSiteDetails
					?.constructionSiteAction?.socialLandlordRental,
			warrantiesTypesSelected: binderWarrantiesTypesSelected?.map(
				(binderWarranty) => binderWarranty.warrantyType,
			),
		},
		isReception,
	);
	const linkFileSubmissionMutation =
		useLinkFileToConstructionSiteSubmission(constructionSiteId);
	const linkFileReceptionMutation =
		useLinkFileToConstructionSiteReception(constructionSiteId);

	const unlinkFileSubmissionMutation =
		useUnlinkFileFromConstructionSiteSubmission(constructionSiteId);
	const deleteFileReceptionMutation =
		useDeleteFileFromConstructionSiteReception(constructionSiteId);

	const linkFileMutation = isReception
		? linkFileReceptionMutation
		: linkFileSubmissionMutation;

	const unlinkFileMutation = isReception
		? deleteFileReceptionMutation
		: unlinkFileSubmissionMutation;

	const filesToUse = isReception
		? receptionOperationFiles || []
		: submissionOperationDetails.filesOnConstructionSiteSubmissions;

	return (
		<div>
			{showHeader && <H3>PIÈCES JUSTIFICATIVES</H3>}
			<div className="bg-white rounded-lg border">
				<Table className="table-fixed w-full">
					<TableHeader>
						<TableRow>
							<TableHead>Type</TableHead>
							<TableHead>Fichier(s)</TableHead>
							<TableHead>Statut</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{documentTypes.map((documentType) => {
							const files = findFilesForDocumentType(documentType, filesToUse);

							return (
								<TableRow key={documentType}>
									<TableCellWithTooltip
										tooltipContent={getDocumentTypeLabel(
											documentType,
											isReception,
										)}
									>
										{getDocumentTypeLabel(documentType, isReception)}
									</TableCellWithTooltip>
									<TableCell className="truncate align-top">
										<div className="flex flex-col gap-2">
											{files.map((file) => (
												<div key={file.file.id}>
													<PreviewFileLink file={file.file} />
												</div>
											))}
										</div>
									</TableCell>
									<TableCell className="truncate align-top">
										<div className="flex flex-col gap-2">
											{files.map((file) => (
												<div
													key={file.file.id}
													className="flex items-center gap-2"
												>
													<FileStatusChip
														humanProcessingStatus={
															file.file.humanProcessingStatus
														}
													/>
													{!disabled && (
														<Button
															variant="ghost"
															size="sm"
															onClick={() => {
																unlinkFileMutation.mutate(file.file.id);
															}}
															disabled={unlinkFileMutation.isPending}
														>
															<LinkBroken02 className="h-4 w-4" />
														</Button>
													)}
												</div>
											))}
											<div>
												{!disabled && (
													<AddDocumentButton
														constructionSiteId={constructionSiteId}
														onUploadSuccess={(fileId) => {
															linkFileMutation.mutate({
																fileId,
																type: documentType,
															});
														}}
													/>
												)}
											</div>
										</div>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</div>
		</div>
	);
};

const getDocumentTypesForSection = (
	section: SubmissionOperationSection,
	additionalDetails?: {
		moaClientType?: ClientType;
		operationTypes?: ActionType[];
		administrativeAuthorizationType?: AdministrativeAuthorizationType | null;
		warrantiesTypesSelected?: (WarrantyType | null)[];
		socialLandlordRental?: boolean | null;
	},
	isReception = false,
) => {
	const documentTypes: ConstructionSiteDocumentType[] = [];
	switch (section) {
		case "MOA": {
			documentTypes.push("PIECE_IDENTITE");
			if (additionalDetails?.moaClientType === "SOCIETY") {
				documentTypes.push("KBIS");
			}
			return documentTypes;
		}
		case "OPERATION_INFORMATION": {
			documentTypes.push("PLANS");
			documentTypes.push("NOTICE_DESCRIPTIVE");
			if (
				additionalDetails?.warrantiesTypesSelected?.some(
					(warrantyType) => warrantyType === "TRC",
				)
			) {
				documentTypes.push("REFERE_PREVENTIF_POUR_AVOISINANTS");
				if (
					additionalDetails.operationTypes?.includes("RENOVATION") ||
					additionalDetails.operationTypes?.includes("EXTENSION")
				) {
					documentTypes.push("CONSTAT_HUISSIER_POUR_EXISTANTS");
				}
			}
			if (additionalDetails?.socialLandlordRental) {
				documentTypes.push("CONTRAT_RESERVATION_BAILLEUR_SOCIAL");
			}
			return documentTypes;
		}
		case "ADMINISTRATIVE_AUTHORIZATION": {
			if (
				additionalDetails?.administrativeAuthorizationType ===
				"DECLARATION_PREALABLE"
			) {
				documentTypes.push("DECLARATION_PREALABLE");
			}
			if (
				additionalDetails?.administrativeAuthorizationType ===
				"PERMIS_CONSTRUIRE"
			) {
				documentTypes.push("PERMIS_CONSTRUIRE");
			}
			return documentTypes;
		}
		case "OPERATION_DATES": {
			if (
				additionalDetails?.warrantiesTypesSelected?.some(
					(warrantyType) => warrantyType === "TRC",
				) &&
				!isReception
			) {
				documentTypes.push("PLANNING_TRAVAUX");
			}
			if (isReception) {
				documentTypes.push("DECLARATION_ACHEVEMENT_TRAVAUX");
				return documentTypes;
			}
			documentTypes.push("DECLARATION_OUVERTURE_CHANTIER");
			return documentTypes;
		}
	}
};

const findFilesForDocumentType = (
	documentType: ConstructionSiteDocumentType,
	files:
		| SubmissionWithOperationDetailsPayload["filesOnConstructionSiteSubmissions"]
		| ReceptionOperationFile[],
) => {
	return files.filter((file) => file.type === documentType);
};

const getDocumentTypeLabel = (
	documentType: ConstructionSiteDocumentType,
	isReception: boolean,
) => {
	switch (documentType) {
		case "PIECE_IDENTITE":
			return "Pièce d'identité du MOA";
		case "KBIS":
			return "KBIS";
		case "PLANS":
			return "Plan de l'opération";
		case "DECLARATION_PREALABLE":
			return "Arrêté de déclaration préalable";
		case "PERMIS_CONSTRUIRE":
			if (isReception) {
				return "Modification du permis de construire";
			}
			return "Permis de construire";
		case "DECLARATION_OUVERTURE_CHANTIER":
			return "Déclaration d'ouverture de chantier";
		case "PLANNING_TRAVAUX":
			return "Planning des travaux";
		case "ATTESTATION_DEMARRAGE":
			return "Attestation du maître d'ouvrage";
		case "REFERE_PREVENTIF_POUR_AVOISINANTS":
			return "Référentiel préventif pour les avoisinants";
		case "CONSTAT_HUISSIER_POUR_EXISTANTS":
			return "Constat d'huissier pour les existants";
		case "CONTRAT_RESERVATION_BAILLEUR_SOCIAL":
			return "Contrat de réservation du bailleur social";
		case "NOTICE_DESCRIPTIVE":
			return "Notice descriptive";
		case "DECLARATION_ACHEVEMENT_TRAVAUX":
			return "Déclaration d'achèvement des travaux";
		default:
			return documentType;
	}
};
